/*
 * Motion for the landing.
 *
 * Two things only, so it reads as one system rather than confetti:
 *   v-reveal   — an element (or a group with a stagger) fades/slides in the first time
 *                it scrolls into view. Adds `is-in`; the CSS does the rest.
 *   reduced()  — true when the visitor asked for reduced motion; the sequences in the
 *                hero and the ask card check it and skip to their end state.
 */
export function reduced() {
    try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
        return false;
    }
}

let observer = null;

function observe(el) {
    if (!('IntersectionObserver' in window) || reduced()) {
        el.classList.add('is-in');
        return;
    }

    if (!observer) {
        observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-in');
                    observer.unobserve(entry.target);
                }
            });
        }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
    }

    observer.observe(el);
}

export const reveal = {
    mounted(el, binding) {
        el.classList.add('reveal');
        /* v-reveal="120" -> this element waits 120ms; v-reveal:stagger on a parent
           gives each child its own step */
        if (binding.arg === 'stagger') {
            Array.from(el.children).forEach((child, i) => {
                child.classList.add('reveal');
                child.style.transitionDelay = `${i * (binding.value || 90)}ms`;
                observe(child);
            });
            el.classList.remove('reveal');
            return;
        }

        if (binding.value) {
            el.style.transitionDelay = `${binding.value}ms`;
        }

        observe(el);
    },
};

/* Counts from 0 to `to` over `ms`, calling `draw(value)` each frame with an eased value. */
export function countUp(to, ms, draw) {
    if (reduced()) { draw(to); return; }

    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    function frame(now) {
        const t = Math.min(1, (now - start) / ms);
        draw(to * ease(t));
        if (t < 1) requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
}

export default {
    install(app) {
        app.directive('reveal', reveal);
    },
};
